import { useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@up2d8/ui';
import { AdminIcon, Btn } from '../admin-kit';
import './huisstijl.css';

/**
 * Het uploadvak van de huisstijl (I1) en van het logo op het platform (P3): gestippelde rand,
 * het huidige beeld, een korte zin en "Vervangen".
 *
 * Het bestand kiezen gaat via een verborgen `<input type="file">` achter de knop; uploaden,
 * voortgang en annuleren doet `useUpload` van het scherm, zoals voorheen met `FileUpload`.
 */
export function UploadBox({
  label,
  hint,
  preview,
  hasImage,
  dark,
  accept,
  progress,
  error,
  busy,
  onSelect,
  onCancel,
  onRemove,
  removeLabel,
  removeTestId,
  testId,
}: {
  label: string;
  hint: ReactNode;
  /** Het huidige beeld, of wat er zonder beeld staat. */
  preview: ReactNode;
  hasImage: boolean;
  /** Het navy vak, zoals de zijbalk. */
  dark?: boolean;
  accept: string;
  progress: number | null;
  error: string | null;
  busy?: boolean;
  onSelect: (file: File) => void;
  onCancel: () => void;
  onRemove?: () => void;
  removeLabel?: string;
  removeTestId?: string;
  testId?: string;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const uploading = progress !== null;

  return (
    <div className="a-sect" data-testid={testId}>
      <div className="a-label">{label}</div>
      <div className={cn('a-empty a-upload', dark && 'dark')}>
        {preview}
        <span className="a-hint">{hint}</span>
        <input
          ref={input}
          type="file"
          accept={accept}
          hidden
          aria-label={label}
          onChange={(event) => {
            const file = event.target.files?.[0];
            // Leegmaken, zodat hetzelfde bestand nog eens kiezen ook iets doet.
            event.target.value = '';
            if (file) onSelect(file);
          }}
        />
        {uploading ? (
          <div className="a-actions" aria-live="polite">
            <span className="a-small">{t('media.uploading', { progress })}</span>
            <Btn variant="ghost" size="sm" onClick={onCancel}>
              {t('common.cancel')}
            </Btn>
          </div>
        ) : (
          <div className="a-actions" style={{ justifyContent: 'center' }}>
            <Btn
              variant="sec"
              size="sm"
              icon={<AdminIcon name="upload" size={16} />}
              disabled={busy}
              aria-label={`${hasImage ? t('beheer.huisstijl.replace') : t('beheer.huisstijl.upload')}: ${label}`}
              onClick={() => input.current?.click()}
            >
              {hasImage ? t('beheer.huisstijl.replace') : t('beheer.huisstijl.upload')}
            </Btn>
            {hasImage && onRemove ? (
              <Btn
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={onRemove}
                testId={removeTestId}
              >
                {removeLabel ?? t('branding.remove')}
              </Btn>
            ) : null}
          </div>
        )}
      </div>
      {error ? (
        <div className="a-error" role="alert">
          <AdminIcon name="alert" size={16} />
          <span>{error}</span>
        </div>
      ) : null}
    </div>
  );
}
